import React, { useState, useEffect } from 'react';
import { 
  Lightbulb, 
  ThumbsUp, 
  Plus, 
  Search, 
  Sparkles, 
  X, 
  Tag, 
  Rocket 
} from 'lucide-react';
import { FeatureBankItem, User } from '../../types';
import { apiService } from '../../utils/apiService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardHeader, CardTitle, CardDescription } from '../ui/card';

interface FeatureBankProps {
  currentUser?: User;
}

export const FeatureBank: React.FC<FeatureBankProps> = ({ currentUser: _currentUser }) => {
  const [items, setItems] = useState<FeatureBankItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Robotics Core',
    complexity: 'Standard',
    status: 'Proposed'
  });

  const categories = [
    'All',
    'Robotics Core',
    'AI & Automation',
    'Hardware & Sensor',
    'Software & UI',
    'Process & Safety',
    'General'
  ];

  useEffect(() => {
    fetchFeatureItems();
  }, []);

  const fetchFeatureItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getFeatureBankItems();
      if (response.data) {
        setItems(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load feature bank items:', err);
      // Fallback sample mock data if API is offline in preview
      setItems(sampleFeatureItems);
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async (id: number | string) => {
    // Optimistic UI update
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, upvotes: item.upvotes + 1 } : item
      )
    );

    try {
      await apiService.upvoteFeatureBankItem(id);
    } catch (err) {
      console.error('Failed to register upvote on backend:', err);
    }
  };

  const handleSubmitNewIdea = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) return;

    setSubmitting(true);
    try {
      await apiService.createFeatureBankItem(formData);
      setIsModalOpen(false);
      setFormData({
        title: '',
        description: '',
        category: 'Robotics Core',
        complexity: 'Standard',
        status: 'Proposed'
      });
      fetchFeatureItems();
    } catch (err: any) {
      console.error('Failed to submit feature idea:', err);
      setError('Could not save feature proposal. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredItems = items.filter((item) => {
    const matchesCategory =
      selectedCategory === 'All' || item.category === selectedCategory;
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case 'Completed':
        return 'default';
      case 'In Progress':
        return 'secondary';
      case 'Planned':
      case 'Under Review':
      default:
        return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <Card className="relative overflow-hidden rounded-xl border border-border bg-card text-card-foreground">
        <CardHeader className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-muted border border-border text-foreground text-xs font-semibold uppercase tracking-wider mb-3">
                <Sparkles className="w-3.5 h-3.5" /> Innovation Hub
              </div>
              <CardTitle className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3">
                <Lightbulb className="w-8 h-8 text-foreground" />
                Feature Bank & R&D Ideas
              </CardTitle>
              <CardDescription className="text-sm mt-2 max-w-2xl">
                Submit, vote, and collaborate on advanced engineering features, robotics algorithms, and workflow enhancements for future RIO PMS release cycles.
              </CardDescription>
            </div>

            <Button
              onClick={() => setIsModalOpen(true)}
              className="h-11 rounded-md"
            >
              <Plus className="w-4 h-4 mr-2" /> Propose New Feature
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Filter and Search Toolbar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 bg-card p-4 rounded-xl border border-border">
        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              className="rounded-md text-xs font-medium whitespace-nowrap"
            >
              {cat}
            </Button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search R&D ideas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 h-9 text-xs"
          />
        </div>
      </div>

      {/* Feature Cards Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className="h-44 bg-muted/40 rounded-xl border border-border animate-pulse"
            />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <Rocket className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-foreground font-semibold text-base">No Feature Ideas Found</h3>
          <p className="text-muted-foreground text-xs mt-1">
            Be the first to submit a proposal for this category!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredItems.map((item) => (
            <Card
              key={item.id}
              className="rounded-xl border border-border bg-card text-card-foreground p-5 transition-all hover:border-foreground/40 flex flex-col justify-between"
            >
              <div>
                {/* Header Row */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="space-y-1">
                    <span className="inline-block px-2 py-0.5 rounded-md bg-muted text-muted-foreground text-[10px] font-semibold tracking-wider">
                      {item.category}
                    </span>
                    <h3 className="text-base font-bold text-foreground">
                      {item.title}
                    </h3>
                  </div>

                  <Badge variant={getStatusBadgeVariant(item.status)}>
                    {item.status}
                  </Badge>
                </div>

                <p className="text-muted-foreground text-xs leading-relaxed line-clamp-3 mb-4">
                  {item.description}
                </p>
              </div>

              {/* Card Footer */}
              <div className="pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-muted-foreground text-[11px]">
                    <Tag className="w-3 h-3 text-muted-foreground" />
                    {item.complexity} Complexity
                  </span>
                  {item.submitterName && (
                    <span className="text-muted-foreground text-[11px]">
                      By {item.submitterName}
                    </span>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpvote(item.id)}
                  className="h-8 gap-1.5 rounded-md"
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                  <span>{item.upvotes}</span>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Submit Proposal Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
          <Card className="w-full max-w-lg bg-card border border-border rounded-xl p-6 shadow-2xl space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-muted-foreground" />
                Submit R&D Proposal
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsModalOpen(false)}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmitNewIdea} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Feature Title *
                </label>
                <Input
                  type="text"
                  required
                  placeholder="e.g., Autonomous ROS2 Navigation Node"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full h-10 rounded-md"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full h-10 bg-background border border-input rounded-md px-3 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {categories.filter((c) => c !== 'All').map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    Estimated Complexity
                  </label>
                  <select
                    value={formData.complexity}
                    onChange={(e) =>
                      setFormData({ ...formData, complexity: e.target.value })
                    }
                    className="w-full h-10 bg-background border border-input rounded-md px-3 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="Basic">Basic</option>
                    <option value="Standard">Standard</option>
                    <option value="Advanced">Advanced</option>
                    <option value="Complex">Complex</option>
                    <option value="Industrial">Industrial</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Detailed Specification / R&D Description *
                </label>
                <textarea
                  required
                  rows={4}
                  placeholder="Describe the feature requirement, targeted benefits, and technical feasibility..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full bg-background border border-input rounded-md p-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Proposal'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

// Sample fallback items for offline/preview mode
const sampleFeatureItems: FeatureBankItem[] = [
  {
    id: 1,
    title: 'Autonomous ROS2 SLAM Navigation Pipeline',
    description: 'Integration of LiDAR and depth camera fusion for indoor autonomous spatial mapping and path planning.',
    category: 'Robotics Core',
    complexity: 'Industrial',
    status: 'In Progress',
    upvotes: 14,
    submitterName: 'Lead Robotics Engineer',
    createdAt: '2026-07-28'
  },
  {
    id: 2,
    title: 'Real-time Telemetry Dashboard via WebSockets',
    description: 'High-frequency CAN bus telemetry streaming to frontend dashboard with sub-50ms latency.',
    category: 'Software & UI',
    complexity: 'Advanced',
    status: 'Planned',
    upvotes: 9,
    submitterName: 'Systems Architect',
    createdAt: '2026-07-27'
  },
  {
    id: 3,
    title: 'Computer Vision Defect Inspection Model',
    description: 'Edge-AI vision module running YOLOv8 ONNX models directly on embedded NPU for automated QC.',
    category: 'AI & Automation',
    complexity: 'Complex',
    status: 'Proposed',
    upvotes: 11,
    submitterName: 'AI Specialist',
    createdAt: '2026-07-26'
  }
];
